import type * as React from 'react'
import { Icon } from '@sunwebgroup/uds-ui/icon'
import { Typography } from '@sunwebgroup/uds-ui/typography'
import cn from 'classnames'
import styles from './Table.module.css'

export { EmptyTableMessage } from './EmptyTableMessage'

export type Breakpoint =
  | 'primitives_breakpoint_m_min'
  | 'primitives_breakpoint_m_max'
  | 'primitives_breakpoint_l_min'

const HIDE_ON_CLASS: Record<Breakpoint, string> = {
  primitives_breakpoint_l_min: styles.hideOnMMax,
  primitives_breakpoint_m_max: styles.hideOnMMax,
  primitives_breakpoint_m_min: styles.hideOnSMax,
}

export interface TableProps extends React.TableHTMLAttributes<HTMLTableElement> {}

export function Table({ className, ...props }: TableProps) {
  return <table className={cn(styles.table, className)} {...props} />
}

export interface TableRowProps extends React.HTMLAttributes<HTMLTableRowElement> {
  $isSelected?: boolean
}

export function TableRow({ className, $isSelected, ...props }: TableRowProps) {
  return (
    <tr
      className={cn(styles.row, $isSelected && styles.rowSelected, className)}
      {...props}
    />
  )
}

export interface TableHeadProps extends React.ThHTMLAttributes<HTMLTableCellElement> {
  $hideOn?: Breakpoint
  $size?: 'lg'
  $alignRight?: boolean
}

export function TableHead({
  $alignRight,
  $hideOn,
  $size,
  className,
  ...props
}: TableHeadProps) {
  return (
    <th
      className={cn(
        styles.head,
        $size === 'lg' && styles.sizeLg,
        $alignRight && styles.alignRight,
        $hideOn && HIDE_ON_CLASS[$hideOn],
        className,
      )}
      {...props}
    />
  )
}

export interface TableCellProps extends React.TdHTMLAttributes<HTMLTableCellElement> {
  $hideOn?: Breakpoint
  $size?: 'lg'
  $isMuted?: boolean
  $alignRight?: boolean
}

export function TableCell({
  $alignRight,
  $hideOn,
  $isMuted,
  $size,
  className,
  ...props
}: TableCellProps) {
  return (
    <td
      className={cn(
        styles.cell,
        $size === 'lg' && styles.sizeLg,
        $alignRight && styles.alignRight,
        $isMuted && styles.muted,
        $hideOn && HIDE_ON_CLASS[$hideOn],
        className,
      )}
      {...props}
    />
  )
}

export function TableHeadSelectable({
  className,
  ...props
}: TableHeadProps) {
  return (
    <TableHead
      className={cn(styles.selectable, className)}
      {...props}
    />
  )
}

export function TableCellSelectable({
  className,
  ...props
}: TableCellProps) {
  return (
    <TableCell
      className={cn(styles.selectable, className)}
      {...props}
    />
  )
}

export function TableHeadGrow({
  className,
  ...props
}: TableHeadProps) {
  return (
    <TableHead
      className={cn(styles.grow, className)}
      {...props}
    />
  )
}

export function TableCellGrow({
  className,
  ...props
}: TableCellProps) {
  return (
    <TableCellWithSubtitle
      className={cn(styles.grow, className)}
      {...props}
    />
  )
}

export function TableCellWithSubtitle({
  className,
  ...props
}: TableCellProps) {
  return (
    <TableCell
      className={cn(styles.cellWithSubtitle, className)}
      {...props}
    />
  )
}

export function Subtitle({
  children,
  className,
  ...props
}: React.ComponentProps<typeof Typography>) {
  return (
    <Typography
      variant="body-s-regular-tight"
      className={cn(styles.subtitle, className)}
      {...props}
    >
      {children}
    </Typography>
  )
}

export function ChevronCell({
  $hideOn,
  className,
  ...props
}: TableCellProps) {
  return (
    <TableCell
      $hideOn={$hideOn}
      className={cn(styles.cellChevron, className)}
      {...props}
    >
      <Icon glyph="ChevronRight" size={20} />
    </TableCell>
  )
}
